import { clearAdminToken, getAdminToken } from './db'
import type {
  AdminLoginResponse,
  AdminStatusResponse,
  ConfigResponse,
  HistoryResponse,
  ScanStatus,
  ScanSubmitRequest,
  ScanSubmitResponse,
} from './api-types'

/**
 * Typed client for the Worker API.
 *
 * Errors carry the server's machine-readable `code` alongside a message that is
 * safe to show a person. The old app rendered `HTTP ${res.status}` at the user,
 * which tells someone holding a phone in a workshop nothing they can act on.
 */

export type ApiErrorCode =
  | 'offline'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'not_configured'
  | 'bad_request'
  | 'conflict'
  | 'webhook_failed'
  | 'webhook_timeout'
  | 'rate_limited'
  | 'internal'
  /** A 2xx whose body was not the JSON the endpoint promises. */
  | 'bad_response'
  | 'unknown'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  /** Present when the server stored the scan but could not deliver it. */
  readonly payload: unknown

  constructor(code: ApiErrorCode, message: string, status = 0, payload: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.payload = payload
  }

  /** True when retrying the same request could plausibly succeed. */
  get retryable(): boolean {
    return (
      this.code === 'offline' ||
      this.code === 'webhook_failed' ||
      this.code === 'webhook_timeout' ||
      this.code === 'internal' ||
      this.code === 'bad_response' ||
      this.code === 'rate_limited'
    )
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /**
   * Attach the admin session token. Only admin endpoints need it — the scanner
   * API is open, matching how the previous app worked.
   */
  admin?: boolean
  signal?: AbortSignal
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 20_000

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, admin = false, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options

  // Fail fast and clearly when there is plainly no connection, rather than
  // waiting out a timeout to say the same thing.
  if (!navigator.onLine) {
    throw new ApiError('offline', 'No connection. Reconnect and try again.')
  }

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  if (admin) {
    const token = await getAdminToken()
    if (token !== null) headers['Authorization'] = `Bearer ${token}`
  }

  // Combine the caller's signal with a timeout so a hung request cannot leave
  // the UI spinning indefinitely.
  const timeout = AbortSignal.timeout(timeoutMs)
  const combined =
    signal !== undefined && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([signal, timeout])
      : (signal ?? timeout)

  // Built conditionally rather than passing `body: undefined`: under
  // exactOptionalPropertyTypes an explicit undefined is not the same as an
  // absent property, and RequestInit does not accept it.
  const init: RequestInit = { method, headers, signal: combined }
  if (body !== undefined) init.body = JSON.stringify(body)

  let response: Response
  try {
    response = await fetch(path, init)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError('internal', 'The server took too long to respond.')
    }
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError('offline', 'Could not reach the server.')
  }

  if (response.status === 204) return undefined as T

  let payload: unknown = null
  let parsed = false
  try {
    payload = await response.json()
    parsed = true
  } catch {
    /* handled below: by the status check when the request failed, and by the
       parse check when it did not */
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error
    const code = (error?.code ?? 'unknown') as ApiErrorCode
    const message = error?.message ?? `Request failed (${response.status}).`

    // An expired or revoked admin session should drop the local token, so the
    // UI shows the passcode prompt again rather than a screen that 401s forever.
    if (response.status === 401 && admin) await clearAdminToken()

    throw new ApiError(code, message, response.status, payload)
  }

  // A 2xx carrying something other than JSON is not a success, and must not be
  // handed back as `null` wearing the response type — the caller reads a field
  // off it and throws somewhere its own try/catch cannot see. HistoryScreen did
  // exactly that: `result.scans` sits inside a setState updater, which React
  // runs after the try block has exited, so the TypeError reached the error
  // boundary and took the whole screen down.
  //
  // The realistic cause on a workshop phone is a captive portal answering every
  // request with its own 200 sign-in page.
  if (!parsed || payload === null) {
    throw new ApiError(
      'bad_response',
      'The server sent a response we could not read. If you are on guest Wi-Fi, you may need to sign in to the network first.',
      response.status,
    )
  }

  return payload as T
}

export const api = {
  getConfig: (signal?: AbortSignal): Promise<ConfigResponse> =>
    request<ConfigResponse>('/api/config', signal === undefined ? {} : { signal }),

  submitScan: (scan: ScanSubmitRequest): Promise<ScanSubmitResponse> =>
    request<ScanSubmitResponse>('/api/scan', {
      method: 'POST',
      body: scan,
      // Generous: the Worker relays to n8n synchronously and waits on it.
      timeoutMs: 25_000,
    }),

  retryScan: (id: string): Promise<ScanSubmitResponse> =>
    request<ScanSubmitResponse>(`/api/scans/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
      timeoutMs: 25_000,
    }),

  getHistory: (params: {
    cursor?: string | null
    status?: ScanStatus | null
    search?: string | null
    workflowId?: string | null
    limit?: number
    signal?: AbortSignal
  }): Promise<HistoryResponse> => {
    const query = new URLSearchParams()
    if (params.cursor != null) query.set('cursor', params.cursor)
    if (params.status != null) query.set('status', params.status)
    if (params.search != null && params.search !== '') query.set('search', params.search)
    if (params.workflowId != null) query.set('workflowId', params.workflowId)
    if (params.limit != null) query.set('limit', String(params.limit))
    return request<HistoryResponse>(
      `/api/history?${query.toString()}`,
      params.signal === undefined ? {} : { signal: params.signal },
    )
  },
}

/** Admin endpoints. Gated by the passcode session, not by Cloudflare Access. */
export const adminApi = {
  status: () => request<AdminStatusResponse>('/api/admin/status'),

  login: (passcode: string) =>
    request<AdminLoginResponse>('/api/admin/login', {
      method: 'POST',
      body: { passcode },
    }),

  logout: () => request<{ ok: boolean }>('/api/admin/logout', { method: 'POST', admin: true }),

  changePasscode: (currentPasscode: string, newPasscode: string) =>
    request<{ ok: boolean }>('/api/admin/passcode', {
      method: 'POST',
      body: { currentPasscode, newPasscode },
      admin: true,
    }),

  listWorkflows: () => request<{ workflows: unknown[] }>('/api/admin/workflows', { admin: true }),
  createWorkflow: (workflow: unknown) =>
    request<{ workflow: unknown }>('/api/admin/workflows', {
      method: 'POST',
      body: workflow,
      admin: true,
    }),
  updateWorkflow: (id: string, workflow: unknown) =>
    request<{ workflow: unknown }>(`/api/admin/workflows/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: workflow,
      admin: true,
    }),
  archiveWorkflow: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/workflows/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      admin: true,
    }),
}
